package br.com.vvrefeicoes.portal.feature.purchases

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.components.AppButton
import br.com.vvrefeicoes.portal.core.ui.components.AppModalCard
import br.com.vvrefeicoes.portal.core.ui.components.AppTextField
import br.com.vvrefeicoes.portal.core.ui.components.PortalButtonVariant
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.core.util.MoneyFormatter
import br.com.vvrefeicoes.portal.domain.model.PurchasesProductRow
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.compras_action_cancel
import portalvv.shared.generated.resources.compras_action_close
import portalvv.shared.generated.resources.compras_action_save
import portalvv.shared.generated.resources.compras_col_consumption
import portalvv.shared.generated.resources.compras_col_measurement
import portalvv.shared.generated.resources.compras_col_product
import portalvv.shared.generated.resources.compras_col_total
import portalvv.shared.generated.resources.compras_col_unit_price
import portalvv.shared.generated.resources.compras_product_details_title
import portalvv.shared.generated.resources.compras_product_edit_title

@Composable
fun PurchasesProductDetailsDialog(
    product: PurchasesProductRow,
    onDismiss: () -> Unit,
) {
    AppModalCard(
        title = stringResource(Res.string.compras_product_details_title),
        onDismiss = onDismiss,
    ) {
        Column(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .widthIn(max = 460.dp),
            verticalArrangement = Arrangement.spacedBy(AppSpacing.space12),
        ) {
            DetailRow(
                label = stringResource(Res.string.compras_col_product),
                value = product.name,
            )
            DetailRow(
                label = stringResource(Res.string.compras_col_measurement),
                value = product.measurement,
            )
            DetailRow(
                label = stringResource(Res.string.compras_col_consumption),
                value = MoneyFormatter.formatNumber(product.consumption),
            )
            DetailRow(
                label = stringResource(Res.string.compras_col_unit_price),
                value = "R$ ${MoneyFormatter.formatCents(product.unitPriceCents)}",
            )
            DetailRow(
                label = stringResource(Res.string.compras_col_total),
                value = "R$ ${MoneyFormatter.formatCents(product.totalCents)}",
            )
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8, Alignment.End),
            ) {
                AppButton(
                    text = stringResource(Res.string.compras_action_close),
                    onClick = onDismiss,
                    variant = PortalButtonVariant.Outlined,
                )
            }
        }
    }
}

@Composable
fun PurchasesProductEditDialog(
    product: PurchasesProductRow,
    onDismiss: () -> Unit,
    onSave: (PurchasesProductRow) -> Unit,
) {
    var name by remember(product) { mutableStateOf(product.name) }
    var measurement by remember(product) { mutableStateOf(product.measurement) }
    var consumption by remember(product) { mutableStateOf(MoneyFormatter.formatNumber(product.consumption)) }
    var unitPrice by remember(product) { mutableStateOf(MoneyFormatter.formatCents(product.unitPriceCents)) }
    var total by remember(product) { mutableStateOf(MoneyFormatter.formatCents(product.totalCents)) }

    val parsedConsumption = consumption.filter { it.isDigit() }.toLongOrNull()
    val parsedUnitPrice = MoneyFormatter.parseReaisToCents(unitPrice)
    val parsedTotal = MoneyFormatter.parseReaisToCents(total)
    val canSave =
        name.isNotBlank() &&
            measurement.isNotBlank() &&
            parsedConsumption != null &&
            parsedUnitPrice != null &&
            parsedTotal != null

    AppModalCard(
        title = stringResource(Res.string.compras_product_edit_title),
        onDismiss = onDismiss,
    ) {
        Column(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .widthIn(max = 460.dp),
            verticalArrangement = Arrangement.spacedBy(AppSpacing.space12),
        ) {
            AppTextField(
                value = name,
                onValueChange = { name = it },
                label = stringResource(Res.string.compras_col_product),
                modifier = Modifier.fillMaxWidth(),
            )
            AppTextField(
                value = measurement,
                onValueChange = { measurement = it },
                label = stringResource(Res.string.compras_col_measurement),
                modifier = Modifier.fillMaxWidth(),
            )
            AppTextField(
                value = consumption,
                onValueChange = { consumption = it },
                label = stringResource(Res.string.compras_col_consumption),
                modifier = Modifier.fillMaxWidth(),
            )
            AppTextField(
                value = unitPrice,
                onValueChange = { unitPrice = it },
                label = stringResource(Res.string.compras_col_unit_price),
                modifier = Modifier.fillMaxWidth(),
            )
            AppTextField(
                value = total,
                onValueChange = { total = it },
                label = stringResource(Res.string.compras_col_total),
                modifier = Modifier.fillMaxWidth(),
            )
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8, Alignment.End),
            ) {
                AppButton(
                    text = stringResource(Res.string.compras_action_cancel),
                    onClick = onDismiss,
                    variant = PortalButtonVariant.Outlined,
                )
                AppButton(
                    text = stringResource(Res.string.compras_action_save),
                    onClick = {
                        onSave(
                            product.copy(
                                name = name.trim(),
                                measurement = measurement.trim(),
                                consumption = parsedConsumption ?: product.consumption,
                                unitPriceCents = parsedUnitPrice ?: product.unitPriceCents,
                                totalCents = parsedTotal ?: product.totalCents,
                            ),
                        )
                    },
                    enabled = canSave,
                )
            }
        }
    }
}

@Composable
private fun DetailRow(
    label: String,
    value: String,
) {
    Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
        Text(
            text = label,
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        Text(
            text = value,
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurface,
        )
    }
}
